import React, { FC, useState, useMemo, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './App.css';
import api from './api';

interface ProjectItem {
  id: string;
  name: string;
  status: 'Ongoing' | 'Completed' | 'Planned';
  summary: string;
  imageUrl: string;
  imageAlt: string;
}

const itemsPerPageOptions = [3, 6, 9, 12];
type ProjectViewType = 'list' | 'grid';

const ProjectsPage: FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [allProjects, setAllProjects] = useState<ProjectItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentView, setCurrentView] = useState<ProjectViewType>('grid');
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState<number>(itemsPerPageOptions[1]); // Default to 6

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await api('/projects');
        setAllProjects(data);
      } catch (err) {
        setError('Failed to fetch projects.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const projectStatuses = useMemo(() => {
    const statuses = new Set(allProjects.map(p => p.status));
    return ['all', ...Array.from(statuses)];
  }, [allProjects]);

  const filteredProjects = useMemo(() => {
    let projects = allProjects;
    if (selectedStatus !== 'all') {
      projects = projects.filter(project => project.status === selectedStatus);
    }
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      projects = projects.filter(project =>
        project.name.toLowerCase().includes(lowerSearchTerm) ||
        project.summary.toLowerCase().includes(lowerSearchTerm)
      );
    }
    return projects;
  }, [allProjects, selectedStatus, searchTerm]);

  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * currentItemsPerPage;
    return filteredProjects.slice(startIndex, startIndex + currentItemsPerPage);
  }, [filteredProjects, currentPage, currentItemsPerPage]);

  const totalPages = Math.ceil(filteredProjects.length / currentItemsPerPage);

  const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleViewChange = (view: ProjectViewType) => {
    setCurrentView(view);
  };
  
  const handleItemsPerPageChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setCurrentItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const handleCreateProject = async () => {
    // ... existing code ...
  };

  if (isLoading) {
    return <div className="page-container"><p>{t('loading_events')}</p></div>;
  }

  if (error) {
    return <div className="page-container"><p>{t('error_loading_events')} {error}</p></div>;
  }

  const renderProjectContent = () => {
    if (paginatedProjects.length === 0) {
      return <p>{t('no_projects_match_filters')}</p>;
    }
    
    const listClassName = currentView === 'list' ? 'event-list list-view' : 'event-list grid-view';
    const cardClassName = currentView === 'list' ? 'event-card-list-item' : 'event-card-grid-item';

    return (
      <div className={listClassName}>
        {paginatedProjects.map(project => (
          <div key={project.id} className={cardClassName}>
            <img src={project.imageUrl} alt={project.imageAlt} className="event-image" />
            <div className="event-details">
              <h3 className="event-name">{project.name}</h3>
              <p className="project-status"><strong>{t('project_status_label')}</strong> {project.status}</p>
              {currentView === 'list' && <p className="event-description">{project.summary}</p>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="events-page-container">
      <h1 className="page-title">{t('projects_page_title')}</h1>

      {user && (
        <div className="page-actions">
          <Link to="/projects/new" className="button-primary">Create Project</Link>
        </div>
      )}

      <div className="project-controls">
        <div className="filter-controls-group">
          <div className="filter-group">
            <label htmlFor="status-filter" className="filter-label">{t('filter_by_status_label')}</label>
            <select id="status-filter" value={selectedStatus} onChange={handleStatusChange} className="filter-select">
              <option value="all">{t('all_statuses_label')}</option>
              {projectStatuses.filter(s => s !== 'all').map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="search-projects" className="filter-label">{t('search_projects_label')}</label>
            <input
              type="text"
              id="search-projects"
              className="search-input"
              placeholder={t('search_placeholder')}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="view-switcher">
          <button onClick={() => handleViewChange('list')} className={`view-button ${currentView === 'list' ? 'active' : ''}`}>{t('view_button_list')}</button>
          <button onClick={() => handleViewChange('grid')} className={`view-button ${currentView === 'grid' ? 'active' : ''}`}>{t('view_button_grid')}</button>
        </div>
      </div>
      
      {renderProjectContent()}

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button onClick={goToPreviousPage} disabled={currentPage === 1} className="pagination-button">{t('pagination_previous')}</button>
          <div className="filter-group">
            <label htmlFor="items-per-page" className="filter-label">{t('items_per_page_label')}</label>
            <select id="items-per-page" value={currentItemsPerPage} onChange={handleItemsPerPageChange} className="filter-select">
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <span className="pagination-info">{t('pagination_page_info', { currentPage, totalPages })}</span>
          <button onClick={goToNextPage} disabled={currentPage === totalPages} className="pagination-button">{t('pagination_next')}</button>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
